import { theme } from '@frameio/vapor-theme';
import type {
  CustomBrand,
  CustomBrandGeneratorOptions,
  HexOrRgbColorString,
} from '../types';
import { isColorLightOrDark, shade, tint, transparentize } from './colorUtils';

const LIGHT_DARK_STEP_INCREMENT = 15;

export type ContrastTextAndIconTokens = Pick<
  CustomBrand,
  'textContrastToken' | 'iconContrastToken' | 'checkboxSvgToken'
>;

/**
 * Takes an rgb or hex color and generates the custom brand className as well as
 * creating the opacities and tints that are used throughout our theming system for brand colors:
 * primaryAlpha, light, and dark.
 * @returns CustomBrand
 */
export const generateCustomBrand = ({
  primaryColor,
}: CustomBrandGeneratorOptions): CustomBrand => {
  return {
    primary: primaryColor,
    primaryAlphaLow: transparentize({
      percentage: 25,
      color: primaryColor,
    }) as HexOrRgbColorString,
    primaryAlphaMedium: transparentize({
      percentage: 50,
      color: primaryColor,
    }) as HexOrRgbColorString,
    dark: shade({
      percentage: LIGHT_DARK_STEP_INCREMENT,
      color: primaryColor,
    }) as HexOrRgbColorString,
    light: tint({
      percentage: LIGHT_DARK_STEP_INCREMENT,
      color: primaryColor,
    }) as HexOrRgbColorString,
    darker: shade({
      percentage: LIGHT_DARK_STEP_INCREMENT * 2,
      color: primaryColor,
    }) as HexOrRgbColorString,
    ...getContrastTextAndIconTokens({ primaryColor }),
  };
};

/**
 * Takes the primary brand color and returns color tokens for text and icon that
 * have the appropriate contrast ratio with the brand color.
 * @returns ContrastTextAndIconTokens
 */
export const getContrastTextAndIconTokens = ({
  primaryColor,
}: CustomBrandGeneratorOptions) => {
  const contrastColor = isColorLightOrDark({
    color: primaryColor,
    returnIfLightColor: 'dark',
    returnIfDarkColor: 'light',
  });
  const checkboxSvgToken = theme.colors.contrast.checkbox.svg[contrastColor];

  return {
    textContrastToken: theme.colors.contrast.text[contrastColor],
    iconContrastToken: theme.colors.contrast.icon[contrastColor],
    checkboxSvgToken,
  };
};
